import { GeometryProviderBase } from "./GeometryProviderBase";
import Geohash from "latlon-geohash";
/**
 * @class GeohashGeometryProvider
 *
 * @classdesc Geometry provider based on geohash cells.
 *
 * @example
 * ```
 * class MyDataProvider extends Mapillary.API.DataProviderBase {
 *      ...
 * }
 *
 * const geohashGeometryProvider = new Mapillary.API.GeohashGeometryProvider();
 * const myDataProvider = new MyDataProvider(geohashGeometryProvider);
 * ```
 */
export class GeohashGeometryProvider extends GeometryProviderBase {
    /**
     * Create a new geohash geometry provider instance.
     *
     * @ignore @param {GeoCoords} [geoCoords] - Optional geo coords instance.
     */
    constructor(collection, geoCoords) {
        super(geoCoords);
        // FIXME: changed from 7 to 10. Just implement own geom provider.
        this._level = 8;
        this._collection = collection;
    }
    setCollection(collection) {
        this._collection = collection;
    }
    /**
     * Encode the minimum set of geohash tiles containing a bounding box.
     *
     * @description The current algorithm does expect the bounding box
     * to be sufficiently small to be contained in an area with the size
     * of maximally four tiles. Up to nine adjacent tiles may be returned.
     * The method currently uses the largest side as the threshold leading to
     * more tiles being returned than needed in edge cases.
     *
     * @param {ILatLon} sw - South west corner of bounding box.
     * @param {ILatLon} ne - North east corner of bounding box.
     *
     * @returns {string} The geohash tiles containing the bounding box.
     */
    bboxToCellIds(sw, ne) {
        return this._bboxSquareToCellIds(sw, ne);
    }
    /** @inheritdoc */
    getCorners(cellId) {
        const [collection, h] = cellId.split("/");
        const bounds = Geohash.bounds(h);
        const nw = { lat: bounds.ne.lat, lon: bounds.sw.lon };
        const ne = { lat: bounds.ne.lat, lon: bounds.ne.lon };
        const se = { lat: bounds.sw.lat, lon: bounds.ne.lon };
        const sw = { lat: bounds.sw.lat, lon: bounds.sw.lon };
        return { nw, ne, se, sw };
    }
    /** @inheritdoc */
    getNeighbors(cellId) {
        /* const [collection, h] = cellId.split("/"); */
        const h = cellId;
        return Geohash.neighbours(h);
    }
    /**
     * Encode the geohash tile for geodetic coordinates.
     *
     * @param {ILatLon} latlon - Latitude and longitude to encode.
     * @param {number} precision - Precision of the encoding.
     *
     * @returns {string} The geohash tile for the lat, lon and precision.
     */
    latLonToCellId(latLon, relativeLevel = 0) {
        const h = Geohash.encode(latLon.lat, latLon.lon, this._level + relativeLevel);
        return `${this._collection}/${h}`;
    }
    /**
     * Encode the geohash tiles within a threshold from a position
     * using Manhattan distance.
     *
     * @param {ILatLon} latlon - Latitude and longitude to encode.
     * @param {number} precision - Precision of the encoding.
     * @param {number} threshold - Threshold of the encoding in meters.
     *
     * @returns {Array<string>} The geohash tiles reachable within the
     * threshold.
     */
    latLonToCellIds(latLon, threshold, relativeLevel = 0) {
        const h = Geohash.encode(latLon.lat, latLon.lon, this._level + relativeLevel);
        const bounds = Geohash.bounds(h);
        const corners = {
            ne: { lat: bounds.ne.lat, lon: bounds.ne.lon },
            nw: { lat: bounds.ne.lat, lon: bounds.sw.lon },
            se: { lat: bounds.sw.lat, lon: bounds.ne.lon },
            sw: { lat: bounds.sw.lat, lon: bounds.sw.lon },
        };
        const neighbours = this.getNeighbors(h);
        return this._filterNeighbors(latLon, threshold, h, corners, neighbours);
    }
    _filterNeighbors(latLon, threshold, cellId, corners, neighbors) {
        const bl = [0, 0, 0];
        const tr = this._geoCoords.geodeticToEnu(corners.ne.lat, corners.ne.lon, 0, corners.sw.lat, corners.sw.lon, 0);
        const position = this._geoCoords.geodeticToEnu(latLon.lat, latLon.lon, 0, corners.sw.lat, corners.sw.lon, 0);
        const left = position[0] - bl[0];
        const right = tr[0] - position[0];
        const bottom = position[1] - bl[1];
        const top = tr[1] - position[1];
        const l = left < threshold;
        const r = right < threshold;
        const b = bottom < threshold;
        const t = top < threshold;
        const cellIds = [cellId];
        if (t) {
            cellIds.push(neighbors.n);
        }
        if (t && l) {
            cellIds.push(neighbors.nw);
        }
        if (l) {
            cellIds.push(neighbors.w);
        }
        if (l && b) {
            cellIds.push(neighbors.sw);
        }
        if (b) {
            cellIds.push(neighbors.s);
        }
        if (b && r) {
            cellIds.push(neighbors.se);
        }
        if (r) {
            cellIds.push(neighbors.e);
        }
        if (r && t) {
            cellIds.push(neighbors.ne);
        }
        return cellIds.map((x) => `${this._collection}/${x}`);
    }
}
//# sourceMappingURL=GeohashGeometryProvider.js.map